import { Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import { formatFileSize } from '@/helpers/file.helper.ts';
import type { MediaUploadStatus } from '@/hooks/use-media-upload.ts';

export default function MediaUploadProgress({
  fileName,
  fileSizeBytes,
  progress,
  status,
  onCancel,
}: {
  fileName: string;
  fileSizeBytes?: number;
  progress: number;
  status: MediaUploadStatus | 'pending';
  onCancel?: () => void;
}) {
  const t = useTranslations();

  const statusLabel =
    status === 'pending'
      ? t('media.uploadProgress.waiting', { defaultValue: 'Waiting…' })
      : status === 'validating'
        ? t('media.uploadProgress.validating')
        : status === 'completing'
          ? t('media.uploadProgress.completing')
          : status === 'success'
            ? t('media.uploadProgress.complete', { defaultValue: 'Complete' })
            : t('media.uploadProgress.uploading', { percent: String(progress) });

  const canCancel =
    onCancel != null &&
    (status === 'pending' || status === 'validating' || status === 'uploading' || status === 'completing');

  return (
    <Box sx={{ width: '100%', mt: 1, mb: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
        <Typography variant="body2" color="text.secondary" noWrap title={fileName} sx={{ flex: 1, minWidth: 0 }}>
          {fileName}
          {fileSizeBytes != null ? ` (${formatFileSize(fileSizeBytes)})` : ''}
        </Typography>
        {canCancel && (
          <Button size="small" color="inherit" onClick={onCancel} sx={{ flexShrink: 0 }}>
            {t('media.uploadProgress.cancel')}
          </Button>
        )}
      </Stack>
      <LinearProgress
        variant={status === 'pending' ? 'indeterminate' : 'determinate'}
        value={status === 'pending' ? undefined : progress}
        sx={{ mt: 0.5 }}
      />
      <Typography variant="caption" color="text.secondary">
        {statusLabel}
      </Typography>
    </Box>
  );
}
